import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Download, Monitor, Globe, Smartphone, CheckCircle, AlertTriangle, Crown, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';
import NavigationMenu from '../components/NavigationMenu';
import DynamicFooter from '../components/DynamicFooter';
import SEOHead from '../components/SEOHead';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const OutlookPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 text-white">
      <SEOHead 
        title="Extension Outlook" 
        description="Téléchargez l'extension SENDBOX pour Microsoft Outlook. Envoyez des fichiers jusqu'à 20 Go directement depuis votre messagerie Outlook." 
      />
      <NavigationMenu />
      <div className="max-w-3xl mx-auto px-6 pt-28 pb-24">

        <div className="flex items-center gap-4 mb-8">
          <img src={`${BACKEND_URL}/outlook/icon-64.png`} alt="SENDBOX" className="w-14 h-14 rounded-xl" />
          <div>
            <h1 className="text-2xl font-bold">Extension Outlook</h1>
            <p className="text-blue-300/60 text-sm">Envoyez des fichiers volumineux directement depuis Outlook</p>
          </div>
          <span className="ml-auto inline-flex items-center gap-1 bg-gradient-to-r from-blue-500 to-cyan-500 text-white text-xs font-bold px-3 py-1 rounded-full">
            <Crown className="w-3 h-3" /> PRO
          </span>
        </div>

        {/* Features */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          {[
            { icon: <Monitor className="w-5 h-5" />, title: 'Outlook Desktop', desc: 'Windows et Mac' },
            { icon: <Globe className="w-5 h-5" />, title: 'Outlook Web', desc: 'outlook.office.com' },
            { icon: <Smartphone className="w-5 h-5" />, title: 'Outlook Mobile', desc: 'iOS et Android' },
          ].map((f, i) => (
            <div key={i} className="bg-slate-800/50 border border-blue-500/20 rounded-xl p-4 text-center">
              <div className="text-blue-400 mb-2 flex justify-center">{f.icon}</div>
              <h3 className="text-white font-medium text-sm">{f.title}</h3>
              <p className="text-blue-300/50 text-xs">{f.desc}</p>
            </div>
          ))}
        </div>

        {/* Download */}
        <div className="bg-slate-800/50 border border-blue-500/20 rounded-2xl p-6 mb-8" data-testid="download-section">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Download className="w-5 h-5 text-blue-400" /> Téléchargement
          </h2>
          <div className="flex flex-col sm:flex-row gap-3">
            <a
              href={`${BACKEND_URL}/api/extensions/outlook-manifest`}
              download="SENDBOX_Outlook_Manifest.xml"
              className="flex-1"
              data-testid="download-manifest-btn"
            >
              <Button className="w-full bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white">
                <Download className="w-4 h-4 mr-2" /> Télécharger le manifest
              </Button>
            </a>
            <a
              href={`${BACKEND_URL}/outlook/taskpane.html`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1"
              data-testid="test-taskpane-btn"
            >
              <Button variant="outline" className="w-full border-blue-500/30 text-blue-300">
                <ExternalLink className="w-4 h-4 mr-2" /> Tester dans le navigateur
              </Button>
            </a>
          </div>
        </div>

        {/* Installation Guide */}
        <div className="bg-slate-800/50 border border-blue-500/20 rounded-2xl p-6 mb-8" data-testid="install-guide">
          <h2 className="text-lg font-semibold text-white mb-6">Guide d'installation</h2>

          <div className="space-y-6">
            <div>
              <h3 className="text-sm font-semibold text-blue-400 uppercase tracking-wider mb-3">Outlook Web (outlook.office.com)</h3>
              <ol className="space-y-2">
                {[
                  'Ouvrez Outlook Web et composez un nouveau message',
                  'Cliquez sur les 3 points (...) dans la barre d\'outils',
                  'Sélectionnez "Obtenir des compléments"',
                  'Cliquez sur "Mes compléments" puis "Ajouter un complément personnalisé"',
                  'Choisissez "Ajouter à partir d\'un fichier" et sélectionnez le manifest.xml téléchargé',
                  'Le bouton SENDBOX apparaît dans la barre d\'outils lors de la composition'
                ].map((step, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-500/20 text-blue-400 text-xs font-bold flex items-center justify-center mt-0.5">{i + 1}</span>
                    <span className="text-blue-300/70 text-sm">{step}</span>
                  </li>
                ))}
              </ol>
            </div>

            <div className="border-t border-blue-500/10 pt-6">
              <h3 className="text-sm font-semibold text-blue-400 uppercase tracking-wider mb-3">Outlook Desktop (Windows)</h3>
              <ol className="space-y-2">
                {[
                  'Ouvrez Outlook et allez dans Fichier > Gérer les compléments',
                  'Cliquez sur "Ajouter un complément" > "Ajouter à partir d\'un fichier"',
                  'Sélectionnez le fichier manifest.xml téléchargé',
                  'Redémarrez Outlook si nécessaire',
                  'Le bouton SENDBOX apparaît dans le ruban lors de la composition d\'un email'
                ].map((step, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-500/20 text-blue-400 text-xs font-bold flex items-center justify-center mt-0.5">{i + 1}</span>
                    <span className="text-blue-300/70 text-sm">{step}</span>
                  </li>
                ))}
              </ol>
            </div>

            <div className="border-t border-blue-500/10 pt-6">
              <h3 className="text-sm font-semibold text-blue-400 uppercase tracking-wider mb-3">Administration Microsoft 365</h3>
              <p className="text-blue-300/60 text-sm mb-2">Pour déployer l'extension pour toute votre organisation :</p>
              <ol className="space-y-2">
                {[
                  'Connectez-vous au Centre d\'administration Microsoft 365',
                  'Allez dans Paramètres > Compléments intégrés',
                  'Cliquez sur "Charger un complément personnalisé"',
                  'Uploadez le manifest.xml et choisissez les utilisateurs cibles',
                  'L\'extension sera automatiquement disponible pour les utilisateurs sélectionnés'
                ].map((step, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-emerald-500/20 text-emerald-400 text-xs font-bold flex items-center justify-center mt-0.5">{i + 1}</span>
                    <span className="text-blue-300/70 text-sm">{step}</span>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>

        {/* Notes */}
        <div className="bg-yellow-500/5 border border-yellow-500/20 rounded-xl p-4 flex gap-3" data-testid="pro-note">
          <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-yellow-300 text-sm font-medium">Abonnement Pro requis</p>
            <p className="text-blue-300/60 text-xs mt-1">Cette extension nécessite un compte SENDBOX Pro actif. Les mêmes limites que votre plan Pro s'appliquent (expiration, téléchargements max, etc.).</p>
          </div>
        </div>
      </div>
      <DynamicFooter fixed={true} />
    </div>
  );
};

export default OutlookPage;
